import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaDiscord,
  FaCrown,
  FaExternalLinkAlt,
  FaSignOutAlt,
  FaSync,
  FaCopy,
  FaCheck,
  FaServer,
  FaGamepad,
  FaHome,
} from 'react-icons/fa';
import Navbar from '../../components/Navbar/Navbar';
import ServerStatus from '../../components/ServerStatus/ServerStatus';
import discordService from '../../services/discordService';
import { fetchPlayerAccount, createPortalSession } from '../../services/accountService';
import { SERVER_HOST } from '../../config/server';
import { VIP_PLANS } from '../../config/vipPlans';
import { TeamBadgeList } from '../../components/TeamBadge/TeamBadge';
import { useI18n } from '../../i18n';
import './Conta.css';

const SupportWidget = lazy(() =>
  import('../../components/SupportChat/SupportWidget')
);

const discordAvatarUrl = (user, size = 128) => {
  if (!user?.id) return null;
  if (user.avatar) {
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=${size}`;
  }
  const index = Number(String(user.id).slice(-2)) % 6;
  return `https://cdn.discordapp.com/embed/avatars/${index}.png`;
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const Conta = () => {
  const { t, get, localeMeta } = useI18n();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [teamProfile, setTeamProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const formatDate = useCallback(
    (iso) => {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleDateString(localeMeta.dateLocale, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      } catch {
        return '—';
      }
    },
    [localeMeta.dateLocale]
  );

  const formatMoney = useCallback(
    (amount, currency = 'BRL') => {
      try {
        return new Intl.NumberFormat(localeMeta.dateLocale, {
          style: 'currency',
          currency: currency === 'BRL' ? 'BRL' : currency,
        }).format(amount);
      } catch {
        return `R$ ${Number(amount).toFixed(2)}`;
      }
    },
    [localeMeta.dateLocale]
  );

  const showToast = useCallback((text) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 2200);
  }, []);

  const loadAccount = useCallback(async (discordUser) => {
    if (!discordUser?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchPlayerAccount(discordUser.id);
      setSubscription(data.subscription || null);
      setPayments(Array.isArray(data.payments) ? data.payments : []);
      setTeamProfile(data.team || null);
    } catch (err) {
      setError(err.message || t('account.errLoad'));
      setSubscription(null);
      setPayments([]);
      setTeamProfile(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const auth = discordService.getPlayerAuth();
    setUser(auth);
    if (auth) {
      loadAccount(auth);
    }
  }, [loadAccount]);

  const connectDiscord = () => {
    window.location.href = discordService.getAuthUrl('account');
  };

  const logout = () => {
    discordService.clearPlayerAuth();
    setUser(null);
    setSubscription(null);
    setPayments([]);
    setError('');
    window.dispatchEvent(new Event('nexus:auth-changed'));
  };

  const openPortal = async () => {
    if (!user?.id) return;
    setPortalLoading(true);
    setError('');
    try {
      const { url } = await createPortalSession(user.id);
      window.location.href = url;
    } catch (err) {
      setError(err.message || t('account.errPortal'));
      setPortalLoading(false);
    }
  };

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      showToast(t('account.toastCopied', { label }));
    } catch {
      showToast(t('account.toastCopyFail'));
    }
  };

  const isActive =
    subscription && ['active', 'past_due', 'trialing'].includes(subscription.status);

  const displayName = user
    ? user.global_name || user.username || t('account.playerFallback')
    : null;

  const avatar = useMemo(() => discordAvatarUrl(user, 160), [user]);

  const renewDays = subscription?.currentPeriodEnd
    ? daysUntil(subscription.currentPeriodEnd)
    : null;

  const cycleLength = subscription?.billing === 'annual' ? 365 : 30;
  const cycleRemainingPct =
    renewDays == null ? null : Math.min(100, Math.round((renewDays / cycleLength) * 100));

  const planMeta = subscription?.planId ? VIP_PLANS[subscription.planId] : null;
  const highlights = subscription?.planId
    ? (get('account.highlights') || {})[subscription.planId] || []
    : [];
  const payStatusMap = get('account.payStatus') || {};

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t('account.greetingMorning');
    if (h < 18) return t('account.greetingAfternoon');
    return t('account.greetingEvening');
  })();

  return (
    <div className="conta">
      <Navbar />

      {toast && (
        <div className="conta-toast" role="status">
          <FaCheck />
          {toast}
        </div>
      )}

      <section className="conta-hero">
        <div className="conta-hero__glow" aria-hidden="true" />
        <div className="conta-hero__blocks" aria-hidden="true" />
        <div className="conta-wrap conta-hero__content">
          <p className="conta-hero__kicker">{t('account.kicker')}</p>
          {user ? (
            <>
              <div className="conta-hero__profile">
                {avatar && (
                  <img
                    src={avatar}
                    alt=""
                    className="conta-hero__avatar"
                    width={72}
                    height={72}
                  />
                )}
                <div>
                  <p className="conta-hero__hello">
                    {greeting}, <span>{displayName}</span>
                  </p>
                  <h1 className="conta-hero__title">{t('account.title')}</h1>
                  {teamProfile?.badges?.length > 0 && (
                    <TeamBadgeList
                      badges={teamProfile.badges}
                      size="md"
                      className="conta-hero__badges"
                    />
                  )}
                  {teamProfile?.title && (
                    <p className="conta-hero__staff-title">{teamProfile.title}</p>
                  )}
                </div>
              </div>
              <p className="conta-hero__lead">
                {t('account.leadLogged')}
              </p>
            </>
          ) : (
            <>
              <h1 className="conta-hero__title">{t('account.title')}</h1>
              <p className="conta-hero__lead">
                {t('account.leadGuest')}
              </p>
            </>
          )}
        </div>
      </section>

      <section className="conta-body">
        <div className="conta-wrap">
          {!user ? (
            <div className="conta-gate">
              <FaDiscord className="conta-gate__icon" />
              <h2>{t('account.gateTitle')}</h2>
              <p>
                {t('account.gateLead')}
              </p>

              <ol className="conta-gate__steps">
                <li>{t('account.gateStep1')}</li>
                <li>{t('account.gateStep2')}</li>
                <li>{t('account.gateStep3')}</li>
              </ol>

              <button
                type="button"
                className="conta-btn conta-btn--primary conta-btn--lg"
                onClick={connectDiscord}
              >
                <FaDiscord />
                {t('account.connectDiscord')}
              </button>
            </div>
          ) : (
            <>
              <div className="conta-toolbar">
                <div className="conta-toolbar__who">
                  {avatar && (
                    <img src={avatar} alt="" className="conta-toolbar__avatar" width={40} height={40} />
                  )}
                  <div>
                    <p className="conta-user__label">{t('account.loggedAs')}</p>
                    <p className="conta-user__name">
                      <FaDiscord />
                      {displayName}
                      {user.username ? ` (@${user.username})` : ''}
                    </p>
                    {teamProfile?.badges?.length > 0 && (
                      <TeamBadgeList
                        badges={teamProfile.badges}
                        size="sm"
                        className="conta-toolbar__badges"
                      />
                    )}
                  </div>
                </div>
                <div className="conta-user__actions">
                  <button
                    type="button"
                    className="conta-btn conta-btn--ghost"
                    onClick={() => loadAccount(user)}
                    disabled={loading}
                  >
                    <FaSync className={loading ? 'is-spin' : ''} />
                    {loading ? t('account.refreshing') : t('account.refresh')}
                  </button>
                  <button type="button" className="conta-btn conta-btn--ghost" onClick={logout}>
                    <FaSignOutAlt />
                    {t('account.logout')}
                  </button>
                </div>
              </div>

              <div className="conta-actions" aria-label={t('account.shortcuts')}>
                <button
                  type="button"
                  className="conta-action"
                  onClick={() => copyText(SERVER_HOST, 'IP')}
                >
                  <FaServer />
                  <span>
                    <strong>{t('account.copyIp')}</strong>
                    <small>{SERVER_HOST}</small>
                  </span>
                </button>
                <a
                  className="conta-action"
                  href="https://discord.gg/9W4Vusc4m2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaDiscord />
                  <span>
                    <strong>{t('common.discord')}</strong>
                    <small>{t('account.discordCommunity')}</small>
                  </span>
                </a>
                <Link className="conta-action" to="/vip">
                  <FaCrown />
                  <span>
                    <strong>{t('account.vipPlans')}</strong>
                    <small>{t('account.vipPlansHint')}</small>
                  </span>
                </Link>
                <Link className="conta-action" to="/">
                  <FaHome />
                  <span>
                    <strong>{t('account.home')}</strong>
                    <small>{t('account.homeHint')}</small>
                  </span>
                </Link>
              </div>

              {error && <p className="conta-error">{error}</p>}

              <div className="conta-layout">
                <div className="conta-main">
                  <article className="conta-panel conta-panel--plan">
                    <header className="conta-panel__head">
                      <FaCrown />
                      <h2>{t('account.activePlan')}</h2>
                    </header>

                    {loading && !subscription ? (
                      <p className="conta-muted">{t('account.loadingPlan')}</p>
                    ) : subscription ? (
                      <div className="conta-plan">
                        <div className="conta-plan__top">
                          <div>
                            <p className="conta-plan__name">{subscription.planName}</p>
                            {planMeta?.tagline && (
                              <p className="conta-plan__tag">{planMeta.tagline}</p>
                            )}
                          </div>
                          <p className={`conta-plan__status is-${subscription.status}`}>
                            {subscription.statusLabel}
                          </p>
                        </div>

                        {renewDays != null && (
                          <div className="conta-cycle">
                            <div className="conta-cycle__row">
                              <span>{t('account.currentCycle')}</span>
                              <strong>
                                {renewDays === 0
                                  ? t('account.renewsToday')
                                  : renewDays === 1
                                    ? t('account.oneDayLeft')
                                    : t('account.daysLeft', { count: renewDays })}
                              </strong>
                            </div>
                            <div
                              className="conta-cycle__bar"
                              role="progressbar"
                              aria-valuenow={cycleRemainingPct ?? 0}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              <span style={{ width: `${cycleRemainingPct ?? 0}%` }} />
                            </div>
                            <p className="conta-cycle__hint">
                              {subscription.paymentMethod === 'pix'
                                ? t('account.validUntil', {
                                    date: formatDate(subscription.currentPeriodEnd),
                                  })
                                : t('account.nextRenewalOn', {
                                    date: formatDate(subscription.currentPeriodEnd),
                                  })}
                            </p>
                          </div>
                        )}

                        <dl className="conta-plan__meta">
                          <div>
                            <dt>{t('account.mcNick')}</dt>
                            <dd>
                              <span>{subscription.minecraftNick || '—'}</span>
                              {subscription.minecraftNick && (
                                <button
                                  type="button"
                                  className="conta-icon-btn"
                                  aria-label={t('account.copyNick')}
                                  onClick={() =>
                                    copyText(subscription.minecraftNick, 'Nick')
                                  }
                                >
                                  <FaCopy />
                                </button>
                              )}
                            </dd>
                          </div>
                          <div>
                            <dt>{t('account.billing')}</dt>
                            <dd>
                              {subscription.billing === 'annual'
                                ? t('account.billingAnnual')
                                : t('account.billingMonthly')}
                            </dd>
                          </div>
                          <div>
                            <dt>{t('account.payment')}</dt>
                            <dd>
                              {subscription.paymentMethod === 'pix'
                                ? t('account.payPix')
                                : t('account.payCard')}
                            </dd>
                          </div>
                          <div>
                            <dt>
                              {subscription.paymentMethod === 'pix'
                                ? t('account.validUntilLabel')
                                : t('account.nextRenewalLabel')}
                            </dt>
                            <dd>{formatDate(subscription.currentPeriodEnd)}</dd>
                          </div>
                        </dl>

                        {highlights.length > 0 && (
                          <div className="conta-perks">
                            <p className="conta-perks__title">{t('account.perksTitle')}</p>
                            <ul>
                              {highlights.map((item) => (
                                <li key={item}>
                                  <FaCheck />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="conta-plan__actions">
                          {subscription.paymentMethod === 'pix' ? (
                            <Link to="/vip" className="conta-btn conta-btn--primary">
                              <FaCrown />
                              {t('account.renewPix')}
                            </Link>
                          ) : subscription.hasStripeCustomer ? (
                            <button
                              type="button"
                              className="conta-btn conta-btn--primary"
                              onClick={openPortal}
                              disabled={portalLoading}
                            >
                              <FaExternalLinkAlt />
                              {portalLoading
                                ? t('account.opening')
                                : isActive
                                  ? t('account.manageRenew')
                                  : t('account.reactivateStripe')}
                            </button>
                          ) : (
                            <Link to="/vip" className="conta-btn conta-btn--primary">
                              <FaCrown />
                              {t('account.subscribePlan')}
                            </Link>
                          )}
                          <Link to="/vip" className="conta-btn conta-btn--ghost">
                            {t('account.comparePlans')}
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="conta-empty">
                        <FaGamepad className="conta-empty__icon" />
                        <p>
                          {t('account.emptyVip')}
                        </p>
                        <Link to="/vip" className="conta-btn conta-btn--primary">
                          <FaCrown />
                          {t('account.viewVip')}
                        </Link>
                      </div>
                    )}
                  </article>

                  <article className="conta-panel">
                    <header className="conta-panel__head">
                      <h2>{t('account.paymentsTitle')}</h2>
                    </header>

                    {loading && payments.length === 0 ? (
                      <p className="conta-muted">{t('account.loading')}</p>
                    ) : payments.length === 0 ? (
                      <p className="conta-muted">
                        {t('account.noPayments')}
                        {subscription?.hasStripeCustomer
                          ? t('account.noPaymentsStripe')
                          : t('account.noPaymentsAfter')}
                      </p>
                    ) : (
                      <ul className="conta-payments">
                        {payments.map((pay) => (
                          <li key={pay.id} className="conta-payment">
                            <div>
                              <p className="conta-payment__desc">{pay.description}</p>
                              <p className="conta-payment__date">{formatDate(pay.created)}</p>
                            </div>
                            <div className="conta-payment__right">
                              <span className="conta-payment__amount">
                                {formatMoney(pay.amount, pay.currency)}
                              </span>
                              <span className={`conta-payment__badge is-${pay.status}`}>
                                {payStatusMap[pay.status] || pay.status}
                              </span>
                              {pay.hostedInvoiceUrl && (
                                <a
                                  href={pay.hostedInvoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="conta-payment__link"
                                >
                                  {t('account.invoice')}
                                </a>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                </div>

                <aside className="conta-side">
                  <article className="conta-panel conta-panel--server">
                    <header className="conta-panel__head">
                      <FaServer />
                      <h2>{t('account.serverTitle')}</h2>
                    </header>
                    <p className="conta-side__lead">
                      {t('account.serverLead')}
                    </p>
                    <ServerStatus variant="panel" />
                    <button
                      type="button"
                      className="conta-btn conta-btn--primary conta-btn--block"
                      onClick={() => copyText(SERVER_HOST, 'IP')}
                    >
                      <FaCopy />
                      {t('account.copyServerIp')}
                    </button>
                  </article>

                  <article className="conta-panel conta-panel--tips">
                    <header className="conta-panel__head">
                      <h2>{t('account.nextSteps')}</h2>
                    </header>
                    <ul className="conta-tips">
                      {!isActive && (
                        <li>
                          <Link to="/vip">{t('account.tipSubscribe')}</Link>
                          {t('account.tipSubscribeRest')}
                        </li>
                      )}
                      <li>
                        {t('account.tipDiscordBefore')}
                        <a
                          href="https://discord.gg/9W4Vusc4m2"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('common.discord')}
                        </a>
                        {t('account.tipDiscordAfter')}
                      </li>
                      {subscription?.minecraftNick && (
                        <li>
                          {t('account.tipNickBefore')}
                          <strong>{subscription.minecraftNick}</strong>
                          {t('account.tipNickAfter')}
                        </li>
                      )}
                    </ul>
                  </article>
                </aside>
              </div>
            </>
          )}
        </div>
      </section>

      <footer className="conta-footer">
        <div className="conta-wrap conta-footer__inner">
          <p className="conta-footer__name">{t('common.brand')}</p>
          <p>{t('common.copyright')}</p>
        </div>
      </footer>

      <Suspense fallback={null}>
        <SupportWidget oauthIntent="account" />
      </Suspense>
    </div>
  );
};

export default Conta;
